import 'lib';
import 'normalize.css/normalize.css';
import './css/styles.css';
import './css/tailwind.scss';
import './fonts/lato/stylesheet.css';

import React from 'react';
import ReactDOM from 'react-dom';
import { ApolloClient, InMemoryCache, ApolloProvider } from '@apollo/client';
import { loadableReady } from '@loadable/component';

import { createUploadLink } from 'apollo-upload-client';

import { Router } from 'react-router';
import createBrowserHistory from 'history/createBrowserHistory';
import cookies from 'js-cookie';
import Bluebird from 'bluebird';
import { History } from 'history';

import polyfillIntl from 'react-intl/polyfill/web-async';
import { URLBuilder } from 'url-builder/url-builder';
import ReactIntlProvider from 'react-intl/intl-provider';

import { UserStore } from 'four-nets/user/user';
import { FourNetsApi } from 'four-nets/api/api';
import { Site } from 'four-nets/site';
import FourNetsSwitch from 'four-nets/four-nets-switch';

import fetchWithProgress from 'apollo-client/fetch-with-progress';

import { Helmet } from 'react-helmet';

import App from './app';
import FourNets from './four-nets';

URLBuilder.defaultURLBuilder.updateUrls(__webpack_require__.url_builder_bundle.urls);

const ssrData = window.__DATA__;

const site = new Site(window.location.hostname);

const cache = new InMemoryCache();

if (ssrData && ssrData.apollo) {
    cache.restore(ssrData.apollo);
}

const api = new FourNetsApi({
    host: window.location.hostname,
    headers: () => {
        const headers: { [key: string]: string } = {};

        const value = cookies.get('csrftoken-new');
        if (value) {
            headers['X-CSRFToken'] = value;
        }

        return headers;
    },
});

const userStore = new UserStore({ api, site });

const csrfToken = cookies.get('csrftoken-new');

const client = new ApolloClient({
    cache,
    headers: {
        'X-CSRFToken': csrfToken,
    },
    ssrForceFetchDelay: 500,
    link: createUploadLink({
        uri: `//${window.location.hostname}/graphql`,
        credentials: 'same-origin',
        headers: {
            'Accept-Language': site.locale,
        },
        fetch: fetchWithProgress,
    }),
    defaultOptions: {
        watchQuery: {
            fetchPolicy: 'cache-and-network',
        },
    },
});

const { locale } = site;

let history: History;

const element = (messages: object, renderError?: Error | null) => {
    if (!history) {
        history = createBrowserHistory({
            forceRefresh: renderError != null,
            getUserConfirmation(message, callback) {
                callback(false);
            },
        });
    }

    return (
        <ReactIntlProvider messages={messages} locale={locale}>
            <Router history={history}>
                <ApolloProvider client={client}>
                    <FourNets
                        serializer={ssrData && ssrData.serializer}
                        api={api}
                        rootHtmlElement={document.getElementById('root')!}
                        site={site}
                        userStore={userStore}
                        userAgent={window.navigator.userAgent}
                        desktopVersion={cookies.get('DesktopVersion') === 'true'}
                    >
                        {({ error, loading, facebookAppId }) => {
                            if (loading) {
                                return null;
                            }

                            return <FourNetsSwitch facebookAppId={facebookAppId} error={renderError || error} />;
                        }}
                    </FourNets>
                </ApolloProvider>
            </Router>
        </ReactIntlProvider>
    );
};

const reactIntlFilename = __webpack_require__.react_intl_bundle[locale];

Bluebird.all([
    fetch(`/${reactIntlFilename}`)
        .then(response => response.json())
        .catch(err => console.error('Failed to load translations.', err)),
    polyfillIntl(),
])
    .then(([messages]) => {
        if (ssrData) {
            return loadableReady(() => {
                ReactDOM.hydrate(
                    <App Helmet={Helmet} origin={window.location.origin}>
                        {element(messages)}
                    </App>,
                    document.getElementById('root')!
                );
            });
        }
        ReactDOM.render(
            <App Helmet={Helmet} origin={window.location.origin}>
                {element(messages)}
            </App>,
            document.getElementById('root')!
        );
    })
    .catch(console.error);
